import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useOnClickOutside } from 'usehooks-ts';

import ChevronLeft from '@/assets/icons/ChevronLeft';
import ChevronRight from '@/assets/icons/ChevronRight';
import { SearchPanelList } from '@/components/search/v1/list/SearchPanelList';

import styles from './CarouselButton.module.css';

type CarouselButtonProps = {
  items: { value: string; label: string }[];
  onChange?: (value: string) => void;
  defaultValue?: string;
  smallVariant?: boolean;
  autoFocus?: boolean;
};

export function CarouselButton({
  items,
  onChange,
  defaultValue,
  smallVariant,
  autoFocus = false,
  ref,
}: CarouselButtonProps & { ref?: React.Ref<HTMLDivElement> }) {
  const [activeIndex, setActiveIndex] = useState(
    defaultValue ? items.findIndex((item) => item.value === defaultValue) : 0,
  );
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const carouselButtonRef = useRef<HTMLDivElement>(null);

  // (temp?) fix for framer-motion type
  // https://github.com/withastro/astro/issues/8195
  const TypedAnimationPresence = AnimatePresence as unknown as any;

  useOnClickOutside(carouselButtonRef as React.RefObject<HTMLElement>, setDropdownIsOpen.bind(null, false));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const handleFocus = () => {
      if (document.activeElement === carouselButtonRef.current) {
        setIsFocused(true);
      }
    };

    const handleBlur = () => {
      if (document.activeElement !== carouselButtonRef.current) {
        setIsFocused(false);
      }
    };

    const element = carouselButtonRef.current;
    if (autoFocus && carouselButtonRef.current) carouselButtonRef.current.focus();
    element?.addEventListener('focus', handleFocus);
    element?.addEventListener('blur', handleBlur);

    return () => {
      element?.removeEventListener('focus', handleFocus);
      element?.removeEventListener('blur', handleBlur);
    };
  }, []);

  const handleOnClick = () => {
    carouselButtonRef?.current?.focus();
    setDropdownIsOpen(!dropdownIsOpen);
  };

  useEffect(() => {
    if (defaultValue) {
      setActiveIndex(items.findIndex((item) => item.value === defaultValue));
    }
  }, [defaultValue]);

  const handleReturnSelectedListItem = (item: string) => {
    setDropdownIsOpen(false);
    setActiveIndex(items.findIndex((i) => i?.label === item));
  };
  const [direction, setDirection] = useState(1);

  const nextItem = () => {
    setActiveIndex((activeIndex + 1) % items.length);
    setDirection(1);
  };

  const prevItem = () => {
    setActiveIndex((activeIndex - 1 + items.length) % items.length);
    setDirection(-1);
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (['ArrowLeft', 'ArrowDown'].includes(event.key)) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (event.key === 'ArrowLeft') prevItem();
    if (event.key === 'ArrowRight') nextItem();
  };

  useEffect(() => {
    onChange?.(items[activeIndex]?.value);
  }, [activeIndex]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div ref={carouselButtonRef} tabIndex={0} onKeyDown={handleOnKeyDown}>
        <div
          style={{ height: smallVariant ? '28px' : '40px', display: 'flex', justifyContent: 'space-between' }}
          className={styles.carouselButtonStyle}
        >
          <button type='button' aria-label='Vorig' className={styles.carouselChevronStyle} onClick={prevItem}>
            <ChevronLeft width='13px' height='13px' aria-hidden='true' />
          </button>
          <TypedAnimationPresence mode='popLayout'>
            <motion.div
              key={items[activeIndex]?.value}
              initial={{ opacity: 0, x: `${-100 * direction}%` }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: `${100 * direction}%` }}
              transition={{
                type: 'spring',
                damping: 30,
                stiffness: 650,
              }}
            >
              <span style={{ cursor: 'pointer' }} data-testid={items[activeIndex]?.value} onClick={handleOnClick}>
                {items[activeIndex]?.label}
              </span>
            </motion.div>
          </TypedAnimationPresence>
          <button type='button' aria-label='Volgend' className={styles.carouselChevronStyle} onClick={nextItem}>
            <ChevronRight width='13px' height='13px' aria-hidden='true' />
          </button>
        </div>
        <SearchPanelList
          centeredList
          dataIsAnArrayOfStrings
          returnOnEnterKeyPressed
          showIcon={false}
          isFocused={isFocused}
          searchPanelIsActive={dropdownIsOpen}
          data={items.map((item) => item?.label)}
          returnSelectedListItem={handleReturnSelectedListItem}
        />
      </div>
    </div>
  );
}
